import { ErrorState } from "@l30/ui";

import { ApiError } from "../api/portal-client.js";

/**
 * Portal equivalent of the staff app's `QueryErrorAlert` (assessment U4).
 *
 * Narrows a React Query error to the canonical `ErrorState` primitive,
 * surfacing `code` + `message` + `requestId` per
 * `docs/design-canon/04-shared/error-codes.md R7`.
 *
 * Why this exists: portal routes were rendering query failures as
 * empty states (`?? 0` counts), silent `<p>Error</p>` markup, or a raw
 * `ErrorState` that dropped the structured `code`/`requestId` — so a
 * 500 was indistinguishable from an empty result and support had
 * nothing to grep. This wrapper turns every query error into an
 * actionable, support-greppable surface in one line:
 *
 *     {query.isError && (
 *       <PortalQueryError error={query.error} retry={() => query.refetch()} />
 *     )}
 *
 * 401/403 never reach here in practice — the global QueryCache handler
 * routes those to sign-in (assessment U1) — but non-API errors
 * (network refused, schema-drift `ApiContractError`) still render with
 * a generic fallback so the surface is consistent regardless of the
 * failure mode.
 */
interface PortalQueryErrorProps {
  error: Error;
  title?: string;
  retry?: () => void;
  className?: string;
}

export function PortalQueryError({ error, title, retry, className }: PortalQueryErrorProps) {
  if (error instanceof ApiError) {
    return (
      <ErrorState
        title={title}
        message={error.message}
        code={error.code}
        requestId={error.requestId ?? undefined}
        retry={retry}
        className={className}
      />
    );
  }

  return (
    <ErrorState
      title={title}
      message={error.message || "Couldn't reach the server. Check your connection and retry."}
      retry={retry}
      className={className}
    />
  );
}
